// MARK://dhk8/pos/pages/OrderPickupPage
// "Pedidos listos para recoger" — pantalla PÚBLICA de cara al cliente.
//
// Es un KDS FILTRADO: NO el tablero de cocina. Reusa el mismo backend
// (`dhk8-kds-tickets`) y el mismo cliente tipado (`lib/clients/kds`), pero
// muestra SOLO el número de orden y su estado — sin items, sin modificadores,
// sin notas de cocina ni acciones operativas. Es la pantalla que cuelga sobre
// la barra para que el comensal sepa cuándo recoger.
//
// - Ruta PÚBLICA: vive FUERA del `Shell` (sin login). El comensal no se
//   autentica; la pantalla la monta el local.
// - `tenantId` viaja por `?tenant=<uuid>` (la pantalla del local se abre con
//   esa URL). Si falta, cae al tenant activo de la sesión POS.
// - Dos columnas: «Preparándose» (IN_PROGRESS) y «Listos» (READY). La columna
//   de Listos es la protagonista — números enormes, alto contraste, chime.
// - Polling cada 5 s (el KDS-board interno usa 8 s; aquí refrescamos más
//   seguido porque es la cara al cliente). Realtime queda como mejora futura.
// - Legible a distancia: números de orden a 7-9 rem, layout de pantalla
//   completa, fondo oscuro.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { listTickets, type KitchenTicketDto } from '../lib/clients/kds.js'
import { getTenantId } from '../lib/api.js'
import BrandMark from '../components/BrandMark.js'

const POLL_MS = 5_000

/** Two-tone chime via WebAudio — suena cuando una orden NUEVA llega a "Listos". */
function chime(): void {
  if (typeof window === 'undefined') return
  try {
    const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }
    const AC = W.AudioContext ?? W.webkitAudioContext
    if (!AC) return
    const ctx = new AC()
    const now = ctx.currentTime
    for (const [freq, at] of [[784, 0], [1047, 0.15], [1319, 0.3]] as const) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = freq
      osc.connect(gain)
      gain.connect(ctx.destination)
      gain.gain.setValueAtTime(0.001, now + at)
      gain.gain.exponentialRampToValueAtTime(0.22, now + at + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.001, now + at + 0.22)
      osc.start(now + at)
      osc.stop(now + at + 0.25)
    }
    setTimeout(() => { void ctx.close().catch(() => {}) }, 900)
  } catch { /* ignore */ }
}

function sortByNumber(a: KitchenTicketDto, b: KitchenTicketDto): number {
  return a.ticketNumber - b.ticketNumber
}

export default function OrderPickupPage() {
  const [searchParams] = useSearchParams()
  const tenantId = searchParams.get('tenant') ?? getTenantId() ?? undefined
  const muted = searchParams.get('mute') === '1'

  const [tickets, setTickets] = useState<KitchenTicketDto[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const knownReady = useRef<Set<string>>(new Set())
  const firstLoad = useRef(true)

  // Reloj — para el encabezado y para calcular "hace X min".
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  // Polling del KDS. Trae el tablero completo y lo filtra a IN_PROGRESS / READY
  // — los únicos estados que le importan al comensal. NOTA: NO se pasa
  // `active:true` a propósito — el servidor define "active" como
  // PENDING|IN_PROGRESS|RECALLED (excluye READY), así que filtrar en el cliente
  // es lo correcto para no perder los pedidos ya listos.
  useEffect(() => {
    // Sin tenant la pantalla no puede consultar el KDS — lo decimos claro en vez
    // de dejar correr un error de validación del backend.
    if (!tenantId) {
      setError('Falta el parámetro ?tenant en la URL de la pantalla.')
      setLoaded(true)
      return
    }
    let cancelled = false
    const ac = new AbortController()
    async function tick() {
      try {
        const all = await listTickets(
          { limit: 200, tenantId },
          ac.signal,
        )
        if (cancelled) return
        const relevant = all.filter((t) => t.status === 'IN_PROGRESS' || t.status === 'READY')
        // Chime cuando una orden NUEVA entra a READY (no en la primera carga).
        const readyNow = relevant.filter((t) => t.status === 'READY')
        if (!firstLoad.current && !muted) {
          const fresh = readyNow.some((t) => !knownReady.current.has(t.id))
          if (fresh) chime()
        }
        knownReady.current = new Set(readyNow.map((t) => t.id))
        firstLoad.current = false
        setTickets(relevant)
        setError(null)
        setLoaded(true)
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'No se pudieron cargar los pedidos')
        setLoaded(true)
      }
    }
    void tick()
    const id = setInterval(() => { void tick() }, POLL_MS)
    return () => { cancelled = true; ac.abort(); clearInterval(id) }
  }, [tenantId, muted])

  const ready = useMemo(() => tickets.filter((t) => t.status === 'READY').sort(sortByNumber), [tickets])
  const preparing = useMemo(() => tickets.filter((t) => t.status === 'IN_PROGRESS').sort(sortByNumber), [tickets])

  // Customer-display surface (Hallazgo 4 / exec-ui-tightening):
  //   La pantalla se ve desde 3-5 m de distancia, montada sobre la barra.
  //   Es SIEMPRE oscura, independiente del tema de la app. En vez de hex
  //   literales (#0d1f17, #9db8a8, etc.) consumimos los tokens declarados
  //   en `theme-nintaigo.css` bajo `[data-surface="display"]` — análogo al
  //   pattern `[data-surface="document"]` que fuerza light para CFDIs.
  return (
    <div
      data-surface="display"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      {/* Encabezado */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem 2.5rem',
          borderBottom: '1px solid var(--display-separator)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <BrandMark />
          <h1 style={{ fontSize: '2rem', fontWeight: 800, margin: 0, letterSpacing: '-0.01em' }}>
            Pedidos listos
          </h1>
        </div>
        <time style={{ fontSize: '2rem', fontWeight: 600, color: 'var(--display-text-tertiary)', opacity: 0.85 }}>
          {new Date(now).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
        </time>
      </header>

      {/* Cuerpo: dos columnas */}
      <main
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1.6fr',
          gap: '2rem',
          padding: '2rem 2.5rem',
          minHeight: 0,
        }}
      >
        {/* Columna: preparándose */}
        <section style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <h2
            style={{
              fontSize: '1.6rem',
              fontWeight: 700,
              margin: '0 0 1rem',
              color: 'var(--display-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Preparándose
          </h2>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
              alignContent: 'flex-start',
              overflowY: 'auto',
            }}
          >
            {preparing.length === 0 && (
              <p style={{ fontSize: '1.4rem', opacity: 0.45 }}>Sin pedidos en preparación</p>
            )}
            {preparing.map((t) => (
              <div
                key={t.id}
                style={{
                  background: 'var(--display-surface)',
                  border: '2px solid var(--display-border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.25rem 1.5rem',
                  minWidth: '7rem',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '4.5rem', fontWeight: 800, lineHeight: 1, color: 'var(--display-text-primary)' }}>
                  {t.ticketNumber}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Columna: listos para recoger */}
        <section style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <h2
            style={{
              fontSize: '1.6rem',
              fontWeight: 800,
              margin: '0 0 1rem',
              color: 'var(--display-ready-eyebrow)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Listos para recoger
          </h2>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1.5rem',
              alignContent: 'flex-start',
              overflowY: 'auto',
            }}
          >
            {ready.length === 0 && loaded && !error && (
              <p style={{ fontSize: '1.6rem', opacity: 0.5 }}>
                Aún no hay pedidos listos. En cuanto tu orden esté lista, aparecerá aquí.
              </p>
            )}
            {ready.map((t) => (
              <div
                key={t.id}
                style={{
                  background: 'linear-gradient(160deg, var(--display-ready-bg-from), var(--display-ready-bg-to))',
                  borderRadius: 'var(--radius-xl)',
                  padding: '2rem 2.75rem',
                  minWidth: '12rem',
                  textAlign: 'center',
                  boxShadow: 'var(--display-ready-shadow)',
                }}
              >
                <div style={{ fontSize: '1.1rem', fontWeight: 700, opacity: 0.85, letterSpacing: '0.08em', color: 'var(--display-ready-text)' }}>
                  ORDEN
                </div>
                <div style={{ fontSize: '8rem', fontWeight: 900, lineHeight: 1, color: 'var(--display-ready-text)' }}>
                  {t.ticketNumber}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Pie de estado */}
      <footer
        style={{
          padding: '0.75rem 2.5rem',
          borderTop: '1px solid var(--display-separator)',
          fontSize: '1rem',
          color: 'var(--display-text-quaternary)',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {error
            ? `Reconectando… (${error})`
            : !loaded
              ? 'Cargando pedidos…'
              : `${ready.length} listo(s) · ${preparing.length} en preparación`}
        </span>
        <span>Actualiza cada 5 s</span>
      </footer>
    </div>
  )
}
